import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { GitBranch, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface OverallProgressCardProps {
  progress: number; // 0-100
  estimatedCompletion: string;
  onOpenGraph?: () => void;
}

export const OverallProgressCard: React.FC<OverallProgressCardProps> = ({
  progress,
  estimatedCompletion,
  onOpenGraph,
}) => {
  return (
    <Card className="border-border/60">
      <CardContent className="p-5 space-y-4">
        {/* Title + percentage */}
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold text-foreground">
            Overall Progress
          </span>
          <span className="text-base font-semibold text-foreground">
            {Math.round(progress)}%
          </span>
        </div>

        {/* Progress bar */}
        <Progress value={progress} className="h-2 bg-correct/20 [&>div]:bg-correct" />

        {/* Estimated completion with tooltip */}
        <div className="flex items-center gap-1.5">
          <p className="text-sm text-muted-foreground">
            Estimated completion{" "}
            <span className="font-semibold text-foreground">
              {estimatedCompletion}
            </span>
          </p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help shrink-0" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-[220px]">
                <p className="text-xs">
                  This course is estimated to be completed by{" "}
                  {estimatedCompletion} if you meet your daily targeted XP.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Course Graph button – auto width, left-aligned */}
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-sm font-medium"
          onClick={onOpenGraph}
        >
          <GitBranch className="h-4 w-4" />
          Course Graph
        </Button>
      </CardContent>
    </Card>
  );
};
